import { faBicycle } from '@fortawesome/pro-regular-svg-icons/faBicycle';
import { faBus } from '@fortawesome/pro-regular-svg-icons/faBus';
import { faCarSide } from '@fortawesome/pro-regular-svg-icons/faCarSide';
import { faClock } from '@fortawesome/pro-regular-svg-icons/faClock';
import { faTrash } from '@fortawesome/pro-regular-svg-icons/faTrash';
import { faWalking } from '@fortawesome/pro-regular-svg-icons/faWalking';
import { faPlusCircle } from '@fortawesome/pro-solid-svg-icons/faPlusCircle';
import NoImportantPlaces from '@propertypal/shared/src/resources/icons/user-section/no-important-places.svg';
import axios, { API_URL } from '@propertypal/shared/src/services/axios';
import { parseApiDate } from '@propertypal/shared/src/services/dayjs';
import { ImportantPlaceResponse, TravelMode } from '@propertypal/shared/src/types/important-places';
import React, { useState } from 'react';
import Loader from 'react-spinners/PulseLoader';
import { useTheme } from 'styled-components';
import FontAwesomeIcon from '../icons/FontAwesomeIcon';
import Checkbox from '../inputs/Checkbox';
import { Label } from '../inputs/Checkbox.style';
import { AddPlaceContainer } from '../property/PropertyImportantPlaces.style.pn';
import { openToast } from '../toast/ToastContext';
import { Text } from '../typography';
import {
  AddToSearch,
  DeleteBtn,
  ImportantPlace,
  ImportantPlacesContainer,
  LoadingBox,
  PlaceBadge,
  PlaceContainer,
  PlaceContent,
  PlaceFooter,
  PlaceHeaderContainer,
  PlaceMarker,
} from './ImportantPlaceList.style.pn';

type ImportantPlaceData = ImportantPlaceResponse[] | undefined;

const travelMapper: Record<TravelMode, any> = {
  BICYCLE: faBicycle,
  DRIVE: faCarSide,
  WALK: faWalking,
  TRANSIT: faBus,
};

interface ImportantPlacesListProps {
  loading: boolean;
  error: any;
  data: ImportantPlaceData;
  setAddModal: (value: React.SetStateAction<boolean>) => void;
  setData: (d: ImportantPlaceData) => void;
  onAddToSearch?: (id: number, checked: boolean) => void;
  searchIds?: number[];
  grid?: boolean;
}

const ImportantPlacesList: React.FC<ImportantPlacesListProps> = ({
  loading,
  error,
  data,
  setAddModal,
  setData,
  onAddToSearch,
  searchIds,
  grid,
}) => {
  const theme = useTheme();

  const [itemLoading, setItemLoading] = useState<number | null>(null);

  const deletePlace = async (id: number) => {
    setItemLoading(id);

    try {
      const result = await axios<ImportantPlaceResponse[]>(
        {
          method: 'DELETE',
          url: `${API_URL}/important-place/${id}`,
        },
        undefined,
      );

      setData(result.data);

      openToast({
        message: 'Important place successfully removed.',
      });
    } catch {
      openToast({
        message: "Something went wrong. We couldn't delete your important place. Please try again!",
        type: 'error',
        title: 'Error',
      });
    } finally {
      setItemLoading(null);
    }
  };

  if (loading)
    return (
      <LoadingBox data-testid="important-places-loading">
        <Loader color={theme.backgroundLight} />
        <Text fontSize={22} mt={15} fontWeight="600">
          Loading your Important Places
        </Text>
      </LoadingBox>
    );

  if (error)
    return (
      <LoadingBox data-testid="important-places-error">
        <PlaceMarker
          src="/map/important-place-marker.svg"
          alt="Important Place Marker"
          style={{ alignSelf: 'center', width: 40, height: 40, marginBottom: 16 }}
        />
        <Text fontSize={22} fontWeight="600">
          Important Places Unavailable
        </Text>
      </LoadingBox>
    );

  if (!data?.length)
    return (
      <LoadingBox data-testid="important-places-empty">
        <Text fontSize={26} fontWeight="600" mb={20} color={theme.keyline}>
          You haven&apos;t added any important places yet.
        </Text>
        <AddPlaceContainer onClick={() => setAddModal(true)} data-testid="add-place-trigger-empty" lg>
          <FontAwesomeIcon icon={faPlusCircle} size={20} />
          <span>Add a place</span>
        </AddPlaceContainer>
        <NoImportantPlaces />
      </LoadingBox>
    );

  return (
    <ImportantPlacesContainer data-testid="important-places-data" $grid={grid}>
      {data?.map((p) => (
        <ImportantPlace
          data-testid="important-place"
          key={p.id}
          style={itemLoading === p.id ? { opacity: 0.5, pointerEvents: 'none' } : undefined}
        >
          <PlaceContainer>
            <div>
              <PlaceHeaderContainer>
                <PlaceMarker src="/map/important-place-marker.svg" alt="Important Place Marker" />
                <DeleteBtn data-testid="important-place-delete" onClick={() => deletePlace(p.id)}>
                  <FontAwesomeIcon icon={faTrash} size={17} />
                </DeleteBtn>
              </PlaceHeaderContainer>
              <PlaceContent>
                <Text fontSize={21} fontWeight="bold">
                  {p.name}
                </Text>
                {p.description && (
                  <Text color="#303036" fontSize={15}>
                    {p.description}
                  </Text>
                )}
              </PlaceContent>
            </div>
            <div>
              <PlaceBadge>
                <span>Up to</span>
                <b> {(p.travelTime || 0) / 60} mins </b>
                <span>away by </span>
                <FontAwesomeIcon icon={travelMapper[p.transport]} size={20} style={{ verticalAlign: 'middle' }} />
              </PlaceBadge>

              {p.arrivalTime && !(p.transport === 'WALK' || p.transport === 'BICYCLE') && (
                <PlaceBadge style={{ marginTop: 10, display: 'block' }}>
                  <FontAwesomeIcon icon={faClock} size={18} style={{ verticalAlign: 'middle', marginRight: 5 }} />
                  <span>Arrive by</span>
                  <b> {parseApiDate(p.arrivalTime).format('h:mm A')}</b>
                  {p.transport !== 'DRIVE' && (
                    <>
                      <span> on a </span>
                      <b>{parseApiDate(p.arrivalTime).format('dddd')}</b>
                    </>
                  )}
                </PlaceBadge>
              )}
            </div>
            {onAddToSearch && (
              <PlaceFooter>
                <AddToSearch>
                  <Checkbox
                    name={`${p.id}`}
                    value={!!searchIds?.includes(p.id)}
                    onValueChange={(checked) => onAddToSearch(p.id, checked)}
                    testID="include-in-search"
                  />
                  <Label htmlFor={`${p.id}`}>Include in search</Label>
                </AddToSearch>
              </PlaceFooter>
            )}
          </PlaceContainer>
        </ImportantPlace>
      ))}
    </ImportantPlacesContainer>
  );
};

export default ImportantPlacesList;
